// src/utils/analytics.js
// Dual-write event tracking: Vercel Analytics (always on when the site is
// deployed to Vercel) + GA4 (only when REACT_APP_FIREBASE_MEASUREMENT_ID
// is set). Vercel is our primary source of truth for the funnel until GA4
// is wired up; the file gracefully no-ops if neither backend is available.

import { track as vercelTrack } from '@vercel/analytics';
import { createLogger } from './logger';
import config from '../config/environment';

const logger = createLogger('Analytics');

let gaInitialized = false;

export function initAnalytics() {
  if (gaInitialized || !config.features.enableAnalytics) return;

  const measurementId = config.firebase.measurementId;
  if (!measurementId) {
    // No warning here — running without GA4 is a valid state; we still
    // have Vercel Analytics. Silent no-op keeps the console clean.
    return;
  }

  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.async = true;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: false });

  gaInitialized = true;
  logger.info('GA4 initialized');
}

/**
 * Vercel Analytics only accepts flat objects of {string | number | boolean | null}.
 * This helper strips undefined and flattens nested values to strings.
 */
function sanitizeProps(params) {
  if (!params || typeof params !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined) continue;
    if (v === null) {
      out[k] = null;
    } else if (
      typeof v === 'string' ||
      typeof v === 'number' ||
      typeof v === 'boolean'
    ) {
      out[k] = v;
    } else {
      // Coerce arrays / nested objects to a short string so we never lose the event.
      out[k] = String(v).slice(0, 100);
    }
  }
  return out;
}

/**
 * Emit an event to every configured analytics backend.
 * Safe to call anywhere — no-ops if no backend is available.
 */
export function trackEvent(eventName, params = {}) {
  const props = sanitizeProps(params);

  // Vercel Analytics (loaded via <Analytics /> in App.js — always on in prod)
  try {
    vercelTrack(eventName, props);
  } catch (err) {
    // vercelTrack throws in local dev with no runtime — silent no-op.
  }

  // GA4 (only when initialized)
  if (gaInitialized && window.gtag) {
    try {
      window.gtag('event', eventName, props);
    } catch (err) {
      logger.warn('Failed to track event to GA4:', eventName, err);
    }
  }
}

export function trackPageView(pagePath) {
  if (gaInitialized && window.gtag) {
    window.gtag('event', 'page_view', { page_path: pagePath });
  }
}
